import { Box, Button, Card, Group, Image, Stack, Text } from '@mantine/core'
import { useEffect, useMemo } from 'react'

import type { InAppMessage, MessageButton } from '../../api/types'
import { AppModal } from '../../components/AppModal'
import { CardTitle } from '../../components/SectionHeader'
import { StatusBanner, type BannerTone } from '../../components/StatusBanner'
import { screenPath, visibleMessages } from './messages'
import { useMessages } from './useMessages'

/** `accent` (docs/plan/notifications.md) is a theme colour key, never free hex; StatusBanner has no dedicated
 * "success" tone, so it reads as the same calm `info` tone as `primary`. */
function bannerTone(accent: InAppMessage['accent']): BannerTone {
  return accent === 'warning' ? 'warning' : 'info'
}

/** A button's target: `screen` resolves to one of the fixed app routes (docs/plan/notifications.md), `url` opens
 * in a new tab. Plain anchors rather than the router's typed `Link`, so the same screen-key → path mapping
 * (`screenPath`) can be reused byte-for-byte by the service worker's notification click handler, which has no
 * router to call into. */
function MessageButtonLink(props: { button: MessageButton; onClick: () => void }) {
  const { action } = props.button
  const external = action.type === 'url'
  return (
    <Button
      component="a"
      href={external ? action.value : screenPath(action.value)}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      variant="light"
      size="compact-sm"
      onClick={props.onClick}
    >
      {props.button.label}
    </Button>
  )
}

/** Marks a `"once"` message as seen when its banner/card/modal is navigated away from (unmounted), so it's shown
 * for the whole visit that first saw it but never shown again after. A `"untilDismissed"` message is left alone
 * here; it's only dismissed by the user's own close/button action. */
function useDismissOnceSeen(message: InAppMessage, dismiss: () => void) {
  useEffect(() => {
    if (message.inapp.frequency !== 'once') return undefined
    return () => dismiss()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the mount/unmount edges matter, not re-renders
  }, [message.id])
}

function MessageBanner(props: { message: InAppMessage; onDismiss: () => void }) {
  const { message } = props
  useDismissOnceSeen(message, props.onDismiss)
  return (
    <Box mb="md">
      <StatusBanner
        tone={bannerTone(message.accent)}
        title={message.title}
        onClose={props.onDismiss}
        action={
          message.buttons.length > 0 ? (
            <Group gap="xs" wrap="nowrap">
              {message.buttons.map((button) => (
                <MessageButtonLink key={button.label} button={button} onClick={props.onDismiss} />
              ))}
            </Group>
          ) : undefined
        }
      >
        {message.body}
      </StatusBanner>
    </Box>
  )
}

function MessageCard(props: { message: InAppMessage; onDismiss: () => void }) {
  const { message } = props
  useDismissOnceSeen(message, props.onDismiss)
  return (
    <Card>
      <CardTitle
        action={
          <Button variant="subtle" color="gray" size="compact-sm" onClick={props.onDismiss}>
            Dismiss
          </Button>
        }
      >
        {message.title}
      </CardTitle>
      {message.imageUrl && <Image src={message.imageUrl} alt="" radius="sm" mb="sm" />}
      <Text size="sm" mb={message.buttons.length > 0 ? 'sm' : 0}>
        {message.body}
      </Text>
      {message.buttons.length > 0 && (
        <Group gap="xs">
          {message.buttons.map((button) => (
            <MessageButtonLink key={button.label} button={button} onClick={props.onDismiss} />
          ))}
        </Group>
      )}
    </Card>
  )
}

function MessageModal(props: { message: InAppMessage; onClose: () => void }) {
  const { message } = props
  useDismissOnceSeen(message, props.onClose)
  return (
    <AppModal opened title={message.title} onClose={props.onClose}>
      <Stack gap="sm">
        {message.imageUrl && <Image src={message.imageUrl} alt="" radius="sm" />}
        <Text size="sm">{message.body}</Text>
        <Group gap="xs" justify="flex-end">
          {message.buttons.map((button) => (
            <MessageButtonLink key={button.label} button={button} onClick={props.onClose} />
          ))}
          <Button variant="subtle" color="gray" onClick={props.onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </AppModal>
  )
}

/**
 * Live in-app messages for the current page (`GET /messages`, docs/plan/notifications.md "In-app"): banners and
 * cards stack above the page content (`Layout.tsx`), one modal at a time. ponytail: cards render in this one shared
 * spot rather than inside each screen's own list (notifications.md's "card in the screen's list") — the screens
 * that would otherwise host one (Today, etc.) are other agents' files right now; move a card in if a screen wants
 * its own placement later.
 */
export function InAppMessages(props: { pathname: string }) {
  const { messages, dismissed, dismiss } = useMessages()
  const visible = useMemo(() => visibleMessages(messages, props.pathname, dismissed), [messages, props.pathname, dismissed])
  const banners = visible.filter((message) => message.inapp.style === 'banner')
  const cards = visible.filter((message) => message.inapp.style === 'card')
  const modal = visible.find((message) => message.inapp.style === 'modal') ?? null

  return (
    <>
      {banners.map((message) => (
        <MessageBanner key={message.id} message={message} onDismiss={() => dismiss(message.id)} />
      ))}
      {cards.length > 0 && (
        <Stack gap="md" mb="md">
          {cards.map((message) => (
            <MessageCard key={message.id} message={message} onDismiss={() => dismiss(message.id)} />
          ))}
        </Stack>
      )}
      {modal && <MessageModal message={modal} onClose={() => dismiss(modal.id)} />}
    </>
  )
}
